import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import styles from './afonsodanielsouza_consultoria.module.scss';
import WhatsappIcon from '../../icons/whatsappIcon.tsx';

const WHATSAPP_NUMBER = '5517981972950';
const WHATSAPP_MSG_CONSULTORIA =
    'Olá! Vi a página de consultoria online (Daniel Assessoria Esportiva) e quero saber qual plano combina comigo. Podemos conversar?';

const whatsappConsultoriaLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG_CONSULTORIA)}`;

/** Valores mensais exibidos no material — totais assumem compromisso no período. */
const PLANO_MENSAL = 300;
const PLANO_TRIMESTRAL_MES = 199;
const PLANO_SEMESTRAL_MES = 177;

const TOTAL_TRIMESTRAL = PLANO_TRIMESTRAL_MES * 3;
const TOTAL_SEMESTRAL = PLANO_SEMESTRAL_MES * 6;

const DIAS_MES = 30;
const DIAS_TRIMESTRE = 90;
const DIAS_SEMESTRE = 180;

const formatBRL = (n: number) =>
    n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const diaMensal = PLANO_MENSAL / DIAS_MES;
const diaTrimestralAvista = TOTAL_TRIMESTRAL / DIAS_TRIMESTRE;
const diaSemestralAvista = TOTAL_SEMESTRAL / DIAS_SEMESTRE;

const GALLERY_ITEMS: { src: string; label: string }[] = [
    {
        src: require('./src/consultoria/consultoria_1.png'),
        label: 'Consultoria de periodização — benefícios e planos',
    },
    {
        src: require('./src/consultoria/consultoria_2.png'),
        label: 'Plano personalizado (nutrição, treino e acompanhamento)',
    },
];

const Afonsodanielsouza_consultoria: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.afonsodanielsouza_consultoria}>
            <div className={styles.page}>
                <div className={styles.header}>
                    <Link
                        to="/afonsodanielsouza"
                        state={{ skipLoader: true }}
                        className={styles.backButton}
                        aria-label="Voltar ao cartão digital"
                    >
                        <FaArrowLeft />
                    </Link>
                    <div className={styles.headerText}>
                        <p className={styles.eyebrow}>Daniel Assessoria Esportiva</p>
                        <h1 className={styles.title}>Consultoria</h1>
                    </div>
                </div>

                <p className={styles.intro}>
                    Treinamento individualizado, revisões periódicas e acompanhamento próximo, com clareza desde o
                    planejamento até o dia a dia. Confira os destaques nas imagens abaixo e os planos com valores
                    atualizados.
                </p>

                <div className={styles.grid}>
                    {GALLERY_ITEMS.map((item, index) => (
                        <figure key={item.src} className={styles.card}>
                            <img
                                src={item.src}
                                alt={`Consultoria online — ${item.label}`}
                                className={styles.cardImage}
                                loading={index < 2 ? 'eager' : 'lazy'}
                            />
                            <figcaption className={styles.cardFigcaption}>{item.label}</figcaption>
                        </figure>
                    ))}
                </div>

                <div className={styles.pricingSection} aria-labelledby="planos-heading">
                    <h2 id="planos-heading" className={styles.pricingTitle}>
                        Planos e valores
                    </h2>

                    <div className={styles.planCard}>
                        <p className={styles.planName}>Mensal</p>
                        <p className={styles.planPeriod}>Renovação mensal (1 mês)</p>
                        <p className={styles.planPrice}>
                            <strong>Valor:</strong> {formatBRL(PLANO_MENSAL)} por mês
                        </p>
                    </div>

                    <div className={styles.planCard}>
                        <p className={styles.planName}>Trimestral</p>
                        <p className={styles.planPeriod}>3 meses — equivalente a {formatBRL(PLANO_TRIMESTRAL_MES)} por mês</p>
                        <p className={styles.planPrice}>
                            <strong>Parcelado:</strong> 3× de {formatBRL(PLANO_TRIMESTRAL_MES)}
                            <br />
                            <strong>À vista:</strong> {formatBRL(TOTAL_TRIMESTRAL)}
                        </p>
                    </div>

                    <div className={styles.planCard}>
                        <p className={styles.planName}>Semestral</p>
                        <p className={styles.planPeriod}>6 meses — equivalente a {formatBRL(PLANO_SEMESTRAL_MES)} por mês</p>
                        <p className={styles.planPrice}>
                            <strong>Parcelado:</strong> 6× de {formatBRL(PLANO_SEMESTRAL_MES)}
                            <br />
                            <strong>À vista:</strong> {formatBRL(TOTAL_SEMESTRAL)}
                        </p>
                    </div>

                    <div className={styles.dailyBox}>
                        <p className={styles.dailyTitle}>Quanto isso custa por dia?</p>
                        <p className={styles.dailyText}>
                            Dividindo o valor total pelo número de dias de cada período, dá para ver o investimento diário
                            na sua saúde, incluindo treino periodizado, ajustes e canal direto de suporte.
                        </p>
                        <ul className={styles.dailyList}>
                            <li>
                                <strong>Mensal ({DIAS_MES} dias):</strong> {formatBRL(PLANO_MENSAL)} ÷ {DIAS_MES} ≈{' '}
                                <strong>{formatBRL(diaMensal)}/dia</strong>
                            </li>
                            <li>
                                <strong>Trimestral (à vista, {DIAS_TRIMESTRE} dias):</strong> {formatBRL(TOTAL_TRIMESTRAL)} ÷{' '}
                                {DIAS_TRIMESTRE} ≈ <strong>{formatBRL(diaTrimestralAvista)}/dia</strong>
                            </li>
                            <li>
                                <strong>Semestral (à vista, {DIAS_SEMESTRE} dias):</strong> {formatBRL(TOTAL_SEMESTRAL)} ÷{' '}
                                {DIAS_SEMESTRE} ≈ <strong>{formatBRL(diaSemestralAvista)}/dia</strong>
                            </li>
                            <li>
                                <strong>No parcelado em mensalidades:</strong> o custo por dia permanece o mesmo ao longo do
                                período ({formatBRL(PLANO_TRIMESTRAL_MES)} ou {formatBRL(PLANO_SEMESTRAL_MES)} por mês),
                                equivalente aos totais acima quando o ciclo é completo.
                            </li>
                        </ul>
                        <p className={styles.dailyHighlight}>
                            Planos mais longos reduzem o custo por dia, você mantém acompanhamento profissional por menos
                            que muitos gastos do dia a dia.
                        </p>
                    </div>
                </div>

                <p className={styles.footerNote}>
                    Valores divulgados para referência; condições de pagamento e vigência podem ser confirmadas no
                    atendimento. Resultados dependem de adesão ao programa e orientações combinadas.
                </p>

                <div className={styles.ctaSection} aria-labelledby="cta-consultoria-heading">
                    <h2 id="cta-consultoria-heading" className={styles.ctaHeading}>
                        Escolha seu plano e comece hoje
                    </h2>
                    <p className={styles.ctaText}>
                        Tire dúvidas sobre mensal, trimestral ou semestral, formas de pagamento e como iniciar sua
                        consultoria. Atendimento pela Daniel Assessoria Esportiva.
                    </p>
                    <a
                        href={whatsappConsultoriaLink}
                        className={styles.ctaWhatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <WhatsappIcon height="22" color="#ffffff" />
                        <span>Falar no WhatsApp agora</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Afonsodanielsouza_consultoria;
